import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, Cloud, CloudOff, Loader2, Plug, RefreshCw } from 'lucide-react'
import clsx from 'clsx'

import { api, mensajeDeError } from '@/lib/api'
import { esCaja } from '@/lib/config'
import { hora } from '@/lib/formato'
import { Campo } from '@/componentes/ui/Campo'
import { Modal } from '@/componentes/ui/Modal'

interface EstadoDeLaCaja {
  conectada: boolean
  pendientes: number
  descartados: number
  ultimoContactoAt?: string | null
  ultimaBajadaAt?: string | null
  ultimoError?: string | null

  /**
   * Cuándo entró algo NUEVO del sistema, que no es lo mismo que la última vez
   * que se habló con la nube.
   *
   * Es la señal con la que esta pantalla vuelve a pedir el catálogo del POS.
   * `ultimaBajadaAt` no sirve para eso: se mueve en cada corrida aunque no
   * venga nada, así que invalidaría la grilla cada cinco minutos por gusto.
   */
  ultimoCambioCatalogoAt?: string | null

  /** Cada cuántos minutos baja sola. Lo dice el servidor, no la pantalla. */
  minutosBajada?: number

  /** Horas desde la última bajada de catálogo. Null si nunca bajó. */
  horasSinBajar?: number | null
  /** Pasó el umbral de aviso: se puede vender, pero hay que decirlo. */
  catalogoViejo?: boolean
  /** Pasó el umbral de bloqueo: el servidor ya no acepta cobrar. */
  catalogoBloqueado?: boolean

  /**
   * La cola no avanza: hay algo esperando hace rato y la nube lo rechaza.
   *
   * Es distinto de estar sin conexión: la nube puede estar contestando bien y
   * rechazar la operación igual —«esa caja no está autorizada», que se arregla
   * volviendo a conectarla—. Sin esto, el chip mostraba «3 por subir» para
   * siempre y el cajero lo leía como «ya va a subir».
   */
  colaTrabada?: boolean
  /**
   * Cuántas rechazó la nube en su último intento.
   *
   * NO es `pendientes`: el ciclo saltea la fila rechazada y sigue, así que las
   * de atrás suben igual. El cartel rojo tiene que decir este número —decir
   * «13 sin poder subir» cuando la nube rechaza una sola es un cartel que
   * exagera, y un cartel que exagera se deja de leer—.
   */
  colaTrabadas?: number
  colaError?: string | null
  colaEsperandoDesde?: string | null
}

/** «hace 3 días», «hace 50 horas». Lo que el cajero puede repetir por teléfono. */
function hace(horas: number): string {
  const dias = Math.floor(horas / 24)

  if (dias >= 1) return `hace ${dias} día${dias === 1 ? '' : 's'}`

  return `hace ${Math.floor(horas)} h`
}

/**
 * Lo mismo pero en MINUTOS, para el detalle de la sincronización.
 *
 * La otra arranca en horas porque mide un corte —«precios de hace 3 días»— y
 * acá la pregunta es la opuesta: si lo que se acaba de cambiar en la oficina
 * ya entró. «hace 4 min» contesta eso y «hace 0 h» no.
 */
function desdeHace(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)

  if (minutos < 1) return 'recién'
  if (minutos < 60) return `hace ${minutos} min`

  return hace(minutos / 60)
}

/**
 * Lo que trae la bajada y la pantalla muestra.
 *
 * Es una lista explícita y no un `['pos']` a secas: invalidar el prefijo
 * entero arrastraría el turno y la balanza, que se sondean solos y no vienen
 * de la nube. Si mañana la bajada trae un recurso nuevo que el POS pinta, va
 * en esta lista —que es justo el lugar donde se busca—.
 */
const CLAVES_DEL_CATALOGO = [
  ['pos', 'productos'],
  ['pos', 'promociones'],
  ['pos', 'medios-pago'],
  ['pos', 'opciones'],
  ['lookups'],
] as const

// El estado del enlace con la nube, en la barra de arriba de la caja.
//
// El host local ya sabía todo esto (EstadoCaja: si la nube contesta, cuántas
// operaciones esperan subir, cuántas se descartaron) y NINGUNA pantalla lo
// mostraba. El cajero podía estar tres días sin conexión, vendiendo bien y
// acumulando ventas que nadie sabía que faltaban: la diferencia entre un corte
// de una hora y uno de tres días es justamente que alguien lo vea.
//
// Por eso el aviso es con TEXTO y no un iconito gris, y dice desde cuándo:
// «Sin conexión desde las 11:40 · 34 por subir» es accionable —el encargado
// mira el módem— y «hace un rato» no.
//
// Los DESCARTADOS son un aviso aparte y más fuerte: una operación descartada
// es plata cobrada que puede no estar en la nube, y no se arregla esperando.
// Al lado va el botón que las vuelve a encolar, porque sin él ese número es
// una noticia sin salida.
export function EstadoSincronizacion() {
  const qc = useQueryClient()
  const [reconectando, setReconectando] = useState(false)
  const [viendoDetalle, setViendoDetalle] = useState(false)

  const consulta = useQuery({
    queryKey: ['caja', 'estado'],
    queryFn: () => api.get<EstadoDeLaCaja>('/estado', { destino: 'local' }),

    // Cada quince segundos: el sincronizador corre cada treinta, así que con
    // esto la pantalla nunca está más de medio ciclo atrasada. Es un pedido a
    // 127.0.0.1 que no toca la base, no un pedido a internet.
    refetchInterval: 15000,

    // Solo en la caja del mostrador: en el navegador esta ruta no existe (la
    // nube no tiene cola de salida) y el aviso de conexión es otro.
    enabled: esCaja(),
  })

  const reintentar = useMutation({
    mutationFn: () => api.post('/sincronizacion/reintentar-descartados'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['caja', 'estado'] }),
  })

  // Cuando entra algo nuevo del sistema, la pantalla vuelve a pedir el
  // catálogo.
  //
  // Sin esto, el POS pedía `/productos/pos` UNA vez y no lo volvía a pedir
  // nunca: el componente se queda montado todo el día y en la caja el refresco
  // al volver a la pestaña está apagado a propósito, así que un precio
  // cambiado en la oficina bajaba a la base local en cinco minutos y la
  // GRILLA seguía mostrando el viejo hasta que alguien cambiara de pantalla.
  // No es cosmético: la cotización la hace el servidor con el precio nuevo,
  // así que el cajero cantaba un número y la caja cobraba otro.
  //
  // El disparo va acá y no en el POS porque esta es la consulta que ya sondea
  // el estado local cada quince segundos: el POS se enteraría igual, pero
  // habría dos sondeos para la misma pregunta.
  const ultimoCambio = consulta.data?.ultimoCambioCatalogoAt ?? null
  const cambioVisto = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    // La primera vuelta solo TOMA NOTA: el catálogo se acaba de pedir al
    // montar, así que invalidarlo ahí sería pedirlo dos veces por gusto.
    if (cambioVisto.current === undefined) {
      cambioVisto.current = ultimoCambio
      return
    }

    if (cambioVisto.current === ultimoCambio) return

    cambioVisto.current = ultimoCambio

    for (const clave of CLAVES_DEL_CATALOGO) {
      void qc.invalidateQueries({ queryKey: clave })
    }
  }, [ultimoCambio, qc])

  const estado = consulta.data
  if (!estado) return null

  const descartados = estado.descartados > 0
  const catalogo = estado.catalogoBloqueado === true || estado.catalogoViejo === true

  // La cola trabada se avisa solo si además hay conexión: sin conexión ya lo
  // dice el chip de al lado, y dos carteles para lo mismo es ruido.
  const trabada = estado.colaTrabada === true && estado.conectada

  return (
    <div className="flex items-center gap-2">
      {/* Catálogo viejo. El aviso es aparte del de conexión porque son dos
          cosas distintas: se puede estar conectado hace un minuto y tener el
          catálogo de hace tres semanas si la bajada viene fallando. Y pasado
          el segundo umbral el servidor NO deja cobrar, así que el cartel tiene
          que decirlo antes de que el cajero lo descubra con un cliente
          adelante. */}
      {catalogo && (
        <div
          className={clsx(
            'flex items-center gap-2 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold',
            estado.catalogoBloqueado === true ? 'bg-rojo text-white' : 'bg-ambar text-white',
          )}
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />

          <span className="hidden md:inline">
            {estado.catalogoBloqueado === true ? 'No se puede cobrar: ' : ''}
            Precios de {hace(estado.horasSinBajar ?? 0)}
          </span>

          <span className="md:hidden">Precios viejos</span>
        </div>
      )}
      {/* La cola trabada. Va en rojo y con el MOTIVO: es plata cobrada que la
          nube no tiene, y la salida no es esperar —hay que volver a conectar
          la caja o llamar a quien pueda—. El mensaje viene del servidor:
          `LeerErrorAsync` saca el `message` del sobre de la API, así que acá
          llega la frase que la nube escribió para una persona y no el JSON
          crudo (que es lo que se veía, con los acentos escapados). */}
      {trabada && (
        <div
          className="flex items-center gap-2 rounded-lg bg-rojo px-3 py-1.5 text-[12.5px]
                     font-semibold text-white"
          title={estado.colaError ?? undefined}
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />

          <span className="hidden md:inline">
            {estado.colaTrabadas ?? estado.pendientes} sin poder subir
            {estado.colaEsperandoDesde ? ` desde las ${hora(estado.colaEsperandoDesde)}` : ''}
            {' — '}
            {estado.colaError ?? 'la nube las rechaza'}
          </span>

          <span className="md:hidden">
            {estado.colaTrabadas ?? estado.pendientes} trabadas
          </span>

          {/* La salida, al lado del problema. Sin este botón el cartel rojo es
              una noticia sin nada que hacer: el motivo más común de una cola
              trabada es que la caja perdió su sesión con la nube, y volver a
              conectarla conserva la instalación, el punto de venta y todo lo
              que hay en la cola. */}
          <button
            type="button"
            onClick={() => setReconectando(true)}
            className="flex items-center gap-1 rounded bg-white/20 px-2 py-0.5 text-[11.5px]
                       hover:bg-white/30"
            title="Volver a conectar esta caja con el sistema"
          >
            <Plug className="h-3.5 w-3.5" />
            Reconectar
          </button>
        </div>
      )}

      {reconectando && <Reconectar onCerrar={() => setReconectando(false)} />}

      {descartados && (
        <div
          className="flex items-center gap-2 rounded-lg bg-rojo px-3 py-1.5 text-[12.5px]
                     font-semibold text-white"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />

          <span>
            {estado.descartados} sin subir
            <span className="hidden sm:inline"> — hay que revisarlas</span>
          </span>

          <button
            type="button"
            onClick={() => reintentar.mutate()}
            disabled={reintentar.isPending}
            className="flex items-center gap-1 rounded bg-white/20 px-2 py-0.5 text-[11.5px]
                       hover:bg-white/30 disabled:opacity-60"
            title="Volver a intentar subirlas"
          >
            {reintentar.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Reintentar
          </button>
        </div>
      )}

      {/* El chip es un BOTÓN y abre el detalle.
          Subir tenía su número a la vista y su botón de reintentar; bajar no
          tenía nada, y «Al día» —que hablaba solo de la cola de subida— se lee
          como «tenés los precios de ahora», que es otra cosa. El detalle
          contesta la pregunta que el local hizo con estas palabras: cambié un
          precio y en la caja no se actualiza, ¿cada cuánto se actualiza? */}
      <button
        type="button"
        onClick={() => setViendoDetalle(true)}
        className={clsx(
          'flex items-center gap-2 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold',
          estado.conectada
            ? 'bg-crema-alt text-tinta-suave hover:bg-borde'
            : 'bg-ambar text-white hover:bg-ambar/90',
        )}
        title={estado.ultimoError ?? 'Ver el estado de la sincronización'}
      >
        {estado.conectada ? (
          <Cloud className="h-4 w-4 shrink-0" />
        ) : (
          <CloudOff className="h-4 w-4 shrink-0" />
        )}

        {/* Sin conexión, las DOS cosas juntas: desde cuándo y cuánto hay en
            cola. «Sin conexión desde las 11:40 · 34 por subir» es lo que hace
            que alguien mire el módem; cada mitad sola no alcanza. */}
        <span className="hidden sm:inline">
          {/* «Todo subido» y no «Al día»: este chip habla de la cola de
              SUBIDA y de nada más, y «al día» se leía como «tenés los precios
              de ahora» —que es justo lo que no garantiza—. */}
          {estado.conectada
            ? estado.pendientes > 0
              ? `${estado.pendientes} por subir`
              : 'Todo subido'
            : [
                `Sin conexión${
                  estado.ultimoContactoAt ? ` desde las ${hora(estado.ultimoContactoAt)}` : ''
                }`,
                estado.pendientes > 0 ? `${estado.pendientes} por subir` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
        </span>

        {/* En mobile no entra el texto, pero el número de pendientes sí: es el
            que el cajero mira antes de apagar la máquina. */}
        {estado.pendientes > 0 && <span className="num sm:hidden">{estado.pendientes}</span>}
      </button>

      {viendoDetalle && (
        <DetalleSincronizacion estado={estado} onCerrar={() => setViendoDetalle(false)} />
      )}
    </div>
  )
}

/**
 * Volver a conectar esta caja con el sistema.
 *
 * Pide las credenciales de quien administra el local —no el PIN del cajero—
 * porque lo que hace es pedirle a la nube una sesión NUEVA para esta
 * instalación. No toca la instalación, ni el punto de venta, ni la cola: es la
 * salida para la caja que se quedó sin sesión sin haber hecho nada mal (le
 * cambiaron la contraseña al usuario con el que se registró, lo dieron de baja,
 * o la nube le cortó la cadena de tokens).
 *
 * Antes existía solo como endpoint y había que llamarlo a mano, así que en la
 * práctica la salida real era borrar la base local y perder las ventas que no
 * habían subido.
 */
function Reconectar({ onCerrar }: { onCerrar: () => void }) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [listo, setListo] = useState<string | null>(null)

  const reconectar = useMutation({
    mutationFn: () =>
      api.post<{ nombre: string }>('/registro/reconectar', { email, password }),

    onSuccess: (r) => {
      setError(null)
      setListo(r.nombre)

      // La cola arranca a moverse en el ciclo siguiente, así que el chip tiene
      // que volver a preguntar: si no, el cartel rojo sigue ahí un rato
      // después de que el problema se arregló.
      void qc.invalidateQueries({ queryKey: ['caja', 'estado'] })
    },

    onError: (e) => {
      setListo(null)
      setError(mensajeDeError(e, 'No se pudo volver a conectar la caja.'))
    },
  })

  const puede = email.trim().length > 0 && password.length > 0 && !reconectar.isPending

  return (
    <Modal
      titulo="Volver a conectar la caja"
      descripcion="Con los datos de quien administra el local. No se pierde nada de lo que está en la cola."
      ancho="sm"
      onCerrar={onCerrar}
      pie={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secundario" onClick={onCerrar}>
            {listo ? 'Cerrar' : 'Cancelar'}
          </button>

          {!listo && (
            <button
              type="button"
              className="btn-primario"
              disabled={!puede}
              onClick={() => reconectar.mutate()}
            >
              {reconectar.isPending ? 'Conectando…' : 'Reconectar'}
            </button>
          )}
        </div>
      }
    >
      {listo ? (
        <p className="text-sm">
          La caja <strong>{listo}</strong> volvió a conectarse. Lo que estaba esperando se sube en
          los próximos minutos.
        </p>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (puede) reconectar.mutate()
          }}
        >
          <Campo
            etiqueta="Correo"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Campo
            etiqueta="Contraseña"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {error && <p className="text-sm font-semibold text-rojo">{error}</p>}
        </form>
      )}
    </Modal>
  )
}

/**
 * El detalle de la sincronización, con el botón que la fuerza.
 *
 * Son DOS cosas distintas y hasta acá el chip mostraba solo una: lo que esta
 * caja le debe al sistema (la cola de subida) y lo que el sistema le debe a
 * esta caja (los precios, las promos, los empleados). La segunda no tenía ni
 * número ni botón, así que el que cambiaba un precio en la oficina se quedaba
 * mirando el mostrador sin saber si faltaban diez segundos o cinco minutos.
 *
 * Lo que el botón hace es BAJAR, no subir: la subida ya corre cada veinte
 * segundos y tiene su propio reintento. Y no borra ni pisa nada —trae lo que
 * la nube ya decidió—, que es por lo que no pide más que el PIN con el que se
 * entró.
 */
function DetalleSincronizacion({
  estado,
  onCerrar,
}: {
  estado: EstadoDeLaCaja
  onCerrar: () => void
}) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [listo, setListo] = useState(false)

  const bajar = useMutation({
    mutationFn: () => api.post('/sincronizacion/bajar', undefined, { destino: 'local' }),

    onSuccess: () => {
      setError(null)
      setListo(true)

      // Las dos mitades: el estado, para que el detalle diga «recién», y el
      // catálogo, que es lo que la persona vino a ver. Sin lo segundo, el
      // botón bajaría el precio nuevo a la base y la grilla seguiría
      // mostrando el viejo —o sea, un botón que parece no hacer nada—.
      void qc.invalidateQueries({ queryKey: ['caja', 'estado'] })

      for (const clave of CLAVES_DEL_CATALOGO) {
        void qc.invalidateQueries({ queryKey: clave })
      }
    },

    onError: (e) => {
      setListo(false)
      setError(mensajeDeError(e, 'No se pudo actualizar.'))
    },
  })

  const cada = estado.minutosBajada ?? 5

  return (
    <Modal
      titulo="Sincronización"
      descripcion="Qué falta subir al sistema y cuándo entró lo último que bajó."
      ancho="sm"
      onCerrar={onCerrar}
      pie={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secundario" onClick={onCerrar}>
            Cerrar
          </button>

          <button
            type="button"
            className="btn-primario"
            disabled={bajar.isPending}
            onClick={() => bajar.mutate()}
          >
            {bajar.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {bajar.isPending ? 'Actualizando…' : 'Actualizar ahora'}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <Renglon titulo="Conexión">
          {estado.conectada
            ? 'El sistema responde.'
            : `Sin conexión${
                estado.ultimoContactoAt ? ` desde las ${hora(estado.ultimoContactoAt)}` : ''
              }.`}
        </Renglon>

        <Renglon titulo="De esta caja al sistema">
          {estado.pendientes > 0
            ? `${estado.pendientes} operación${estado.pendientes === 1 ? '' : 'es'} esperando subir.`
            : 'No hay nada esperando: todo lo que se cobró acá ya subió.'}
        </Renglon>

        {/* La mitad que faltaba, y con el «cada cuánto» que el local
            preguntó. El número sale del servidor porque es un ajuste de la
            máquina y puede estar en otro valor. */}
        <Renglon titulo="Del sistema a esta caja">
          {estado.ultimoCambioCatalogoAt
            ? `Lo último que entró —precios, promociones, empleados— llegó ${desdeHace(
                estado.ultimoCambioCatalogoAt,
              )}.`
            : 'Desde que arrancó el programa no entró nada nuevo.'}{' '}
          Se actualiza sola cada {cada} minuto{cada === 1 ? '' : 's'}.
        </Renglon>

        {listo && !bajar.isPending && (
          <p className="flex items-center gap-1.5 font-semibold text-verde-oscuro">
            <Check className="h-4 w-4" />
            Listo: la pantalla ya está con lo último.
          </p>
        )}

        {error && <p className="font-semibold text-rojo">{error}</p>}
      </div>
    </Modal>
  )
}

function Renglon({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <p className="flex flex-col gap-0.5">
      <span className="text-[12.5px] font-semibold uppercase tracking-wide text-tinta-tenue">
        {titulo}
      </span>

      <span className="text-tinta-suave">{children}</span>
    </p>
  )
}
